import { type ComponentsPropsList, alpha } from '@mui/material/styles';

import { createTheme } from '../theme';

const theme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          'input::placeholder': {
            overflow: 'visible',
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
        variant: 'outlined',
      },
      styleOverrides: {
        outlined: ({ theme: { unstable_sx: sx, ...theme } }) =>
          sx({
            color: theme.palette.text.primary,
            fontSize: 14,
            fontWeight: 600,
            transform: 'translate(0, 1.5px) scale(1)',
            zIndex: 0,
            /*
          `~` = General sibling combinator
          The line below is to check that if this classname is same sibling level
          */
            '~.MuiOutlinedInput-root': {
              mt: 3,
            },
            '&.Mui-focused, &.Mui-error, &.Mui-disabled': {
              color: theme.palette.text.primary,
            },
          }),
        asterisk: ({ theme: { unstable_sx: sx } }) => sx({ color: theme.palette.error.main }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: { unstable_sx: sx, ...theme }, ownerState }) => {
          const color = ownerState?.color as ComponentsPropsList['MuiOutlinedInput']['color'];
          return sx({
            bgcolor: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey['300']}`,
            borderRadius: '4px',
            fieldset: {
              display: 'none',
            },
            ':hover': {
              borderColor: theme.palette[color ?? 'primary']['main'],
            },
            '&.Mui-focused': {
              borderColor: theme.palette[color ?? 'primary']['main'],
              boxShadow: `${alpha(theme.palette[color ?? 'primary']['main'], 0.25)} 0 0 0 3px`,
            },
            '&.Mui-error': {
              borderColor: theme.palette.error.main,
              '&.Mui-focused': {
                boxShadow: `${alpha(theme.palette.error.main, 0.25)} 0 0 0 3px`,
              },
              '&.Mui-disabled': {
                boxShadow: 'none',
              },
            },
            '&.Mui-disabled': {
              bgcolor: theme.palette.grey[100],
              ':hover': {
                borderColor: theme.palette.grey['300'],
              },
              '&.Mui-error': {
                borderColor: theme.palette.error.main,
              },
            },
          });
        },
        adornedStart: ({ theme: { unstable_sx: sx } }) => sx({ input: { pl: 1 } }),
        adornedEnd: ({ theme: { unstable_sx: sx } }) => sx({ input: { pr: 1 } }),
        input: ({ theme: { unstable_sx: sx } }) =>
          sx({
            // Input height 42px + border 2px = 44px
            minHeight: '42px',
            boxSizing: 'border-box',
            fontSize: 16,
            lineHeight: 1.5,
          }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: ({ theme: { unstable_sx: sx, ...theme } }) =>
          sx({
            mx: 0,
            mt: 0.5,
            color: theme.palette.text.secondary,
            // Thai has no inter-word spaces, so the browser would otherwise look for break
            // opportunities inside a Thai run and can end a line on a pre-posed vowel
            // (ไ เ แ โ ใ), which must stay attached to the consonant after it. keep-all
            // suppresses those implicit opportunities so wrapping happens only at real
            // spaces; break-word is the fallback so a run that still cannot fit wraps
            // instead of overflowing.
            wordBreak: 'keep-all',
            overflowWrap: 'break-word',
            '&.Mui-disabled': {
              color: theme.palette.text.secondary,
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          }),
      },
    },
  },
});

export default theme.components;
